import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Exhibition } from '@/shared/types';
import { useImageUrl } from '@/shared/hooks/useImageUrl';
import headphoneIcon from '@/assets/svg/headphone_icon.svg';
import walkIcon from '@/assets/svg/walk_icon.svg';
import checkIconCircle from '@/assets/svg/check_icon_circle.svg';
import draftIcon from '@/assets/svg/draft_icon.svg';

interface ExhibitionCardProps {
  exhibition: Exhibition;
  isDragging: boolean;
  onDragStart: (exhibitionId: string) => void;
  onDragOver: (e: React.DragEvent, exhibitionId: string, place: 'before' | 'after') => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, exhibitionId: string, place: 'before' | 'after') => void;
  onDragEnd: () => void;
}

export const ExhibitionCard: React.FC<ExhibitionCardProps> = ({
  exhibition,
  isDragging,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const name =
    exhibition.name?.fr ||
    Object.values(exhibition.name || {}).find(Boolean) ||
    t('exhibitions.noTitle');
  const imageUrl = useImageUrl(exhibition.cover);
  const episodesCount = exhibition.episodesNumber?.['fr'] || 0;
  const durationMinutes = exhibition.durationTimeInMinutes?.['fr'] || 0;
  const duration = `${durationMinutes} ${t('exhibitions.minutes')}`;
  const [isDraggingState, setIsDraggingState] = useState(false);

  const handleDragStart = (e: React.DragEvent) => {
    setIsDraggingState(true);
    onDragStart(exhibition.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', exhibition.id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const midX = rect.left + rect.width / 2;
    const place: 'before' | 'after' = e.clientX < midX ? 'before' : 'after';
    onDragOver(e, exhibition.id, place);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingState(false);
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const midX = rect.left + rect.width / 2;
    const place: 'before' | 'after' = e.clientX < midX ? 'before' : 'after';
    onDrop(e, exhibition.id, place);
  };

  const handleCardClick = () => {
    if (isDraggingState) {
      setIsDraggingState(false);
      return;
    }
    navigate(`exhibitions/${exhibition.id}`);
  };

  const frenchStatus = exhibition.status?.['fr'] || 'draft';

  return (
    <div
      className={`exhibition-card ${isDragging ? 'dragging' : ''}`}
      onClick={handleCardClick}
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragLeave={onDragLeave}
      onDrop={handleDrop}
      onDragEnd={() => {
        setIsDraggingState(false);
        onDragEnd();
      }}
    >
      <div className="card-image">
        {imageUrl ? (
          <img src={imageUrl} alt={name} />
        ) : (
          <div className="image-placeholder">{t('exhibitions.imagePlaceholder')}</div>
        )}
        <div className="drag-dots">
          <div className="drag-dots-container">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <div className="drag-dots-container">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
      <div className="card-content">
        <h3 className="card-title">{name}</h3>
        <div className="card-details">
          <div className="detail-item">
            <img src={headphoneIcon} alt="" className="detail-icon" />
            <span>
              {episodesCount} {t('exhibitions.episodes')}
            </span>
          </div>
          <div className="detail-item">
            <img src={walkIcon} alt="" className="detail-icon" />
            <span>{duration}</span>
          </div>
        </div>
        {frenchStatus === 'published' ? (
          <div className="card-badge published">
            <img src={checkIconCircle} alt="" className="badge-icon" />
            <span>{t('exhibitions.published')}</span>
          </div>
        ) : (
          <div className="card-badge draft">
            <img src={draftIcon} alt="" className="badge-icon" />
            <span>{t('exhibitions.draft')}</span>
          </div>
        )}
      </div>
    </div>
  );
};
